/**
 * React Query hooks for ticket sales campaigns — the list, one campaign, its
 * change history, and the create / edit writes.
 *
 * A successful write puts the fresh campaign into its own cache entry and
 * marks the list and that campaign's history stale, since both just changed.
 * Errors arrive as `TicketSalesCampaignApiError`, whose `status` and
 * `fieldErrors` say what to show.
 */
import type {
  TicketSalesCampaign,
  TicketSalesCampaignCreateRequest,
  TicketSalesCampaignPatchRequest,
} from '@bonsai/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  createTicketSalesCampaign,
  fetchTicketSalesCampaign,
  fetchTicketSalesCampaignHistory,
  fetchTicketSalesCampaigns,
  updateTicketSalesCampaign,
  type CampaignListParams,
} from '../../api/ticket-sales-campaigns';

export const TICKET_SALES_CAMPAIGNS_KEY = ['ticket-sales-campaigns'] as const;
export const ticketSalesCampaignListKey = (params: CampaignListParams = {}) =>
  [...TICKET_SALES_CAMPAIGNS_KEY, 'list', params] as const;
export const ticketSalesCampaignKey = (id: string) =>
  [...TICKET_SALES_CAMPAIGNS_KEY, 'detail', id] as const;
export const ticketSalesCampaignHistoryKey = (id: string) =>
  [...TICKET_SALES_CAMPAIGNS_KEY, 'history', id] as const;

export function useTicketSalesCampaigns(params: CampaignListParams = {}) {
  return useQuery({
    queryKey: ticketSalesCampaignListKey(params),
    queryFn: ({ signal }) => fetchTicketSalesCampaigns(params, signal),
  });
}

export function useTicketSalesCampaign(id: string) {
  return useQuery({
    queryKey: ticketSalesCampaignKey(id),
    queryFn: ({ signal }) => fetchTicketSalesCampaign(id, signal),
    enabled: id.length > 0,
  });
}

export function useTicketSalesCampaignHistory(id: string) {
  return useQuery({
    queryKey: ticketSalesCampaignHistoryKey(id),
    queryFn: ({ signal }) => fetchTicketSalesCampaignHistory(id, signal),
    enabled: id.length > 0,
  });
}

function applySaved(qc: QueryClient, campaign: TicketSalesCampaign): void {
  qc.setQueryData(ticketSalesCampaignKey(campaign.id), campaign);
  void qc.invalidateQueries({ queryKey: [...TICKET_SALES_CAMPAIGNS_KEY, 'list'] });
  void qc.invalidateQueries({ queryKey: ticketSalesCampaignHistoryKey(campaign.id) });
}

export function useCreateTicketSalesCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TicketSalesCampaignCreateRequest) => createTicketSalesCampaign(body),
    onSuccess: (campaign) => applySaved(qc, campaign),
  });
}

export function useUpdateTicketSalesCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: TicketSalesCampaignPatchRequest) => updateTicketSalesCampaign(id, patch),
    onSuccess: (campaign) => applySaved(qc, campaign),
  });
}
